import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  TextField,
  TextFieldProps,
  createFilterOptions
} from '@mui/material';
import { removeDuplicatesByKey } from 'components/common/Requests/helpers/helpers';
import { useEffect, useMemo, useState } from 'react';
import { SegmentTag } from 'types/global';
import { filterOptions } from '../../../api/services/ReferenceDataService';
import { isBottomReached } from './isBottomReached';

const PLACEHOLDER = 'Start typing or click to select ';

const filter = createFilterOptions<OptionInternal>();

export type UniversalSelectProps = {
  segment?: SegmentTag;
  creatable: boolean;
  disabled?: boolean;
  filter?: string;
  sort?: string;
  label?: string;
  TextFieldProps?: Partial<TextFieldProps>;
  AutocompleteProps?: Partial<
    AutocompleteProps<OptionInternal, true, any, undefined>
  >;
} & ({ type: string } | { defaultOptions: Option[] });

export type Option = {
  value: string;
  label: string;
};

type OptionInternal = Option & {
  addNewValueOption?: boolean;
  isValue?: boolean;
};

export function UniversalSelect(
  props: UniversalSelectProps & {
    value: Option[];
    onChange: (newValue: Option[]) => void;
  }
) {
  const typeForRemoteOptions = 'type' in props ? props.type : undefined;

  const [options, setOptions] = useState<OptionInternal[]>(() =>
    'defaultOptions' in props ? props.defaultOptions : []
  );
  const [page, setPage] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [searchState, setSearchState] = useState('');

  const segmentFilter = props.segment ?? null;

  const handleInputChange = (input: string) => {
    setSearchState(input);
  };

  const onMenuScrollDown = () => {
    if (typeForRemoteOptions) {
      if (hasMorePages)
        filterOptions(
          searchState,
          typeForRemoteOptions,
          segmentFilter,
          props.filter,
          undefined,
          (response: any) => {
            if (response.length === 0) {
              setHasMorePages(false);
              return;
            }
            const newOptions = [...options, ...response];
            setOptions(removeDuplicatesByKey(newOptions, 'label'));

            setPage(curr => curr + 1);
          },
          () => {},
          page + 1,
          props.sort
        );
    }
  };

  useEffect(() => {
    if (typeForRemoteOptions) {
      filterOptions(
        searchState,
        typeForRemoteOptions,
        segmentFilter,
        props.filter,
        undefined,
        (response: any) => {
          setOptions(removeDuplicatesByKey(response, 'label'));
          setPage(0);
          setHasMorePages(true);
        },
        () => {},
        0,
        props.sort
      );
    }
  }, [
    typeForRemoteOptions,
    props.filter,
    searchState,
    segmentFilter,
    props.sort
  ]);

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      margin="normal"
      placeholder={PLACEHOLDER}
      label={props.label}
      {...props.TextFieldProps}
    />
  );

  /**
   * Provide an empty array as default value, to prevent switch from uncontrolled to controlled component.
   * Memoize it because of: https://github.com/mui/material-ui/issues/32425
   */
  const value = useMemo<OptionInternal[]>(
    () => props.value ?? [],
    [props.value]
  );

  /**
   * Add the current values to the options, otherwise the component will complain that
   * values are missed in the options.
   */
  const realOptions = useMemo(
    () => [
      ...options,
      ...value // load options which are selected, but not in the currently loaded pages
        .filter(v => !options.some(o => o.value === v.value)) // prevent duplicates
        .map(o => ({ ...o, isValue: true }))
    ],
    [value, options]
  );

  /**
   * And we hide the values again, because they would confuse the user.
   */
  const filterOutValuesAgain = (options: OptionInternal[]) =>
    options.filter(o => !o.isValue);

  const defaultProps = {
    value: value,
    fullWidth: true,
    renderInput: renderInput,
    disabled: props.disabled,
    disableCloseOnSelect: true,
    isOptionEqualToValue: (option: OptionInternal, value: OptionInternal) =>
      option.value === value.value,
    ...props.AutocompleteProps
  };

  return 'defaultOptions' in props ? (
    <Autocomplete
      {...defaultProps}
      multiple={true}
      disabled={props.disabled}
      options={props.defaultOptions}
      onChange={(_, newValue) => {
        if (Array.isArray(newValue)) {
          props.onChange(newValue);
        } else {
          props.onChange(newValue ? [newValue] : []);
        }
      }}
    />
  ) : (
    <Autocomplete
      {...defaultProps}
      multiple={true}
      disabled={props.disabled}
      options={realOptions}
      getOptionLabel={option =>
        option.addNewValueOption ? `Add "${option.label}"` : option.label
      }
      onChange={(_, newValue) =>
        props.onChange(
          newValue.map(o => {
            delete o.addNewValueOption;
            return o;
          })
        )
      }
      onInputChange={(_, newInputValue) => handleInputChange(newInputValue)}
      ListboxProps={{
        onScroll: event => {
          if (isBottomReached(event.currentTarget)) {
            onMenuScrollDown();
          }
        }
      }}
      filterOptions={
        props.creatable
          ? (options, params) => {
              const filtered = filter(options, params);

              const { inputValue } = params;
              // Suggest the creation of a new value
              const isExisting = options.some(
                option => inputValue === option.label
              );
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  value: inputValue,
                  label: inputValue,
                  addNewValueOption: true
                });
              }

              return filterOutValuesAgain(filtered);
            }
          : options => filterOutValuesAgain(options)
      }
    />
  );
}
